import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import axios from "axios";

import * as actionTypes from "../../../store/actions/actionTypes";
import {linkBookingFile} from "../../../store/actions/customUI";
import AdmissionBulkDocumentsPdf from "../../AdmissionBulkDocumentsPdf";
import Spinner from "../../Spinner";


/**
 * Booking-link section of the RFA/consent upload modal (shown only when the
 * ManagerCustomUIAdmissionListUploadPdf has bookingLink_enable). Lets a manager
 * reuse a PDF already attached to a booking instead of downloading and
 * re-uploading it. Auto-matched bookings are shown on open; a search box is the
 * fallback when nothing matches.
 */
const UploadPdf_SelectBooking = ({admission, customUI, onFileLinked}) => {
    const dispatch = useDispatch();
    const idToken = useSelector(state => state.auth.idToken);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [matches, setMatches] = useState([]);
    const [results, setResults] = useState([]);
    const [search, setSearch] = useState('');
    const [searched, setSearched] = useState(false);
    // When set, the section swaps to an inline PDF viewer for the chosen booking
    // file (same pattern as the pdfGenerator_enable "Print" toggle).
    const [previewFile, setPreviewFile] = useState(null);

    const apiUrl = `${process.env.REACT_APP_API_PATH}/admission/${admission.id}/custom-ui/${customUI.id}/booking-candidates`;

    const doLoad = (searchTerm = '') => {
        setLoading(true);
        setError('');
        axios.get(apiUrl, {headers: {'Authorization': idToken}, params: {search: searchTerm}})
            .then((response) => {
                setLoading(false);
                if (response.data.error) {
                    setError(response.data.error);
                    return;
                }
                setMatches(response.data.matches || []);
                setResults(response.data.results || []);
            })
            .catch(() => {
                setLoading(false);
                setError('Failed to load bookings');
            });
    };

    // Auto-match on open.
    useEffect(() => {
        doLoad('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const doSearch = event => {
        event.preventDefault();
        setSearched(true);
        doLoad(search);
    };

    const doLink = async bookingFileId => {
        const updatedAdmission = await dispatch(linkBookingFile(admission, customUI, bookingFileId));
        // In multiple-uploads mode keep the modal open (return to the booking
        // list) so the manager can link several booking files in a row.
        if (customUI.multipleUploads) {
            if (onFileLinked && updatedAdmission) {
                onFileLinked(updatedAdmission);
            }
            setPreviewFile(null);
        } else {
            dispatch({type: actionTypes.MODAL_CUSTOM_UI_UPLOAD_PDF_HIDE});
        }
    };

    // Inline PDF viewer (reuses AdmissionBulkDocumentsPdf, which fetches the file
    // as a blob and renders it in an iframe with Print/Download).
    if (previewFile) {
        return <>
            <div className="d-flex justify-content-between align-items-center">
                <h4>Preview - {previewFile.name}</h4>
                <button type="button" className="btn btn-outline-primary" onClick={() => setPreviewFile(null)}>
                    Back to the list
                </button>
            </div>
            <AdmissionBulkDocumentsPdf admissionId={admission.id}
                                       action={`${previewFile.url}?token=${idToken}`}
                                       title={previewFile.name} downloadName={previewFile.name} width="100%"
                                       actions={({disabled}) =>
                                           <button type="button" className="btn btn-sm btn-primary" disabled={disabled}
                                                   onClick={() => doLink(previewFile.id)}>
                                               Link as {customUI.columnTitle || 'RFA'}
                                           </button>
                                       }/>
            <hr />
        </>;
    }

    const renderBooking = booking => (
        <div key={booking.id} className="booking-link-row" style={{borderTop: '1px solid #eee', padding: '.5rem 0'}}>
            <div>
                <strong>{booking.surname}, {booking.firstName}</strong>
                {' '}— {booking.procedureDate}
                {booking.phone ? ` — ${booking.phone}` : ''}
                {booking.surgeon ? ` — ${booking.surgeon}` : ''}
            </div>
            {booking.warning &&
                <div className="text-warning" style={{fontSize: '.85rem'}}>⚠ {booking.warning}</div>}
            {booking.files.map(file => (
                <div key={file.id} className="d-flex justify-content-between align-items-center" style={{marginTop: '.25rem', gap: '.5rem'}}>
                    <span style={{minWidth: 0, overflowWrap: 'anywhere'}}>{file.originalName || file.fileName}</span>
                    <span style={{flexShrink: 0, whiteSpace: 'nowrap'}}>
                        <button type="button" className="btn btn-sm btn-primary"
                                onClick={() => setPreviewFile({id: file.id, url: file.previewUrl, name: file.originalName || file.fileName})}>
                            Preview
                        </button>
                    </span>
                </div>
            ))}
        </div>
    );

    return <>
        <h4>Link from booking</h4>
        {error && <p className="error">{error}</p>}
        {loading ? <Spinner /> : <>
            {matches.length > 0
                ? <>
                    <p>Matching bookings for this admission:</p>
                    {matches.map(renderBooking)}
                </>
                : <p>No booking automatically matched this admission. Search below.</p>
            }

            <form className="person-info-form" onSubmit={doSearch} style={{marginTop: '.75rem'}}>
                <div className="d-flex" style={{gap: '.5rem'}}>
                    <input type="text" className="form-control" placeholder="Search bookings by name, phone, surgeon"
                           value={search} onChange={e => setSearch(e.target.value)}/>
                    <button type="submit" className="btn btn-primary" disabled={loading} style={{whiteSpace: 'nowrap'}}>
                        Search
                    </button>
                </div>
            </form>

            {searched && (results.length > 0
                ? <div style={{marginTop: '.5rem'}}>{results.map(renderBooking)}</div>
                : <p style={{marginTop: '.5rem'}}>No bookings found for this search.</p>)}
        </>}
        <hr />
    </>;
};

export default UploadPdf_SelectBooking;
